import { Fragment, RefObject, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/shared/DropdownMenu";
import Icon from "~/components/shared/Icon";
import Indicator from "~/components/shared/Indicator";
import { Tooltip } from "~/components/shared/Tooltip";
import CategoryOptions from "~/components/build/Show/components/Filter/CategoryOptions";
import { FilterCategory, SelectedFilter } from "~/components/build/Show/components/Filter/types";

type Props = {
  categories: FilterCategory[];
  selected: SelectedFilter[];
  onChange: (next: SelectedFilter[]) => void;
  /** Test id for the trigger button. Defaults to "filter-menu". */
  testId?: string;
  /** Label shown in the trigger. Defaults to "Filter". */
  triggerLabel?: string;
  /**
   * Ref to the element the menu should stay within. Passed as a ref (rather than
   * the resolved element) and read on open, so the boundary resolves correctly on
   * the first open even though the element isn't mounted during the parent's
   * initial render.
   */
  collisionBoundaryRef?: RefObject<HTMLElement | null>;
};

// Cap the list height to the available space so long option lists (e.g.
// branches) stay scrollable inside the viewport.
//
// This lives on an inner wrapper rather than on DropdownMenuContent itself.
// While the menu animates open, the content briefly carries a transform, which
// makes it the containing block for Radix's absolutely-positioned arrow. If
// `overflow` sat on the content, the arrow (which is painted just outside the
// content box, pointing at the trigger) would be clipped for the duration of
// the animation and only appear once it finished — a visible flicker. Keeping
// overflow off the content lets the arrow render throughout.
const scrollableListClassName =
  "max-h-[var(--radix-dropdown-menu-content-available-height,50vh)] overflow-y-auto py-1";

const groupLabelClassName =
  "text-charcoal-400 px-3 py-1 text-xs font-medium normal-case tracking-normal";

/**
 * Dropdown filter menu. Renders each `FilterCategory` as an inline group: a
 * heading row (the category label) followed by the category's checkbox
 * options. Groups are visually separated. Selecting an option toggles it via
 * `onChange` without closing the menu.
 */
export default function FilterMenu({
  categories,
  selected,
  onChange,
  testId = "filter-menu",
  triggerLabel = "Add filter",
  collisionBoundaryRef,
}: Props) {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Tooltip tooltip={triggerLabel} placement="bottom" disabled={open}>
        <DropdownMenuTrigger asChild={true}>
          <button
            type="button"
            data-testid={testId}
            className="text-charcoal-700 inline-flex items-center justify-center rounded-md bg-gray-100 p-1.5 transition-colors hover:bg-purple-100 hover:text-purple-600 focus:z-10"
          >
            <span className="relative inline-flex">
              <Icon icon="custom/outline/16x/filter" className="h-4 w-4 shrink-0" />
              {selected.length > 0 && (
                <Indicator
                  animate={false}
                  size="sm"
                  color="purple"
                  className="absolute right-0 top-0 -translate-y-1/2 translate-x-1/2"
                />
              )}
            </span>
          </button>
        </DropdownMenuTrigger>
      </Tooltip>

      <DropdownMenuPortal>
        <DropdownMenuContent
          className="p-0"
          collisionBoundary={collisionBoundaryRef?.current}
          collisionPadding={0}
          data-testid={`${testId}-content`}
        >
          <div className={scrollableListClassName}>
            {categories.map((category, index) => (
              <Fragment key={category.key}>
                {index > 0 && <DropdownMenuSeparator className="mx-0 my-1 bg-gray-300" />}
                <DropdownMenuLabel
                  className={groupLabelClassName}
                  data-testid={`${testId}-${category.key}-label`}
                >
                  {category.label}
                </DropdownMenuLabel>
                <CategoryOptions
                  category={category}
                  selected={selected}
                  onChange={onChange}
                  testId={testId}
                />
              </Fragment>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
